// Small, accessible filter-chip row for the /alerts list page.
//
// Each group (severity, state) is a <div role="group"> with a
// visually-hidden label. Chips are real <button>s so keyboard
// navigation and focus rings work out of the box. Active chip is
// distinguished visually AND via aria-pressed.
//
// Kept intentionally dumb — the parent owns the filter state and
// passes it in + an onChange callback. No hooks here so the chips
// can be used outside a QueryProvider in tests.

const SEVERITY_OPTIONS = [
  { value: null, label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

const STATE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'all', label: 'All' },
];

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
        active
          ? 'bg-blue-600 border-blue-600 text-white'
          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export default function FilterChips({ filters, onChange, agents = [] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
      <div role="group" aria-labelledby="severity-label" className="flex items-center gap-2">
        <span id="severity-label" className="text-xs font-medium text-gray-500 uppercase">
          Severity
        </span>
        {SEVERITY_OPTIONS.map((opt) => (
          <Chip
            key={String(opt.value)}
            active={filters.severity === opt.value}
            onClick={() => onChange({ ...filters, severity: opt.value })}
          >
            {opt.label}
          </Chip>
        ))}
      </div>

      <div role="group" aria-labelledby="state-label" className="flex items-center gap-2">
        <span id="state-label" className="text-xs font-medium text-gray-500 uppercase">
          State
        </span>
        {STATE_OPTIONS.map((opt) => (
          <Chip
            key={opt.value}
            active={filters.state === opt.value}
            onClick={() => onChange({ ...filters, state: opt.value })}
          >
            {opt.label}
          </Chip>
        ))}
      </div>

      {agents.length > 1 && (
        <div role="group" aria-labelledby="agent-label" className="flex items-center gap-2">
          <span id="agent-label" className="text-xs font-medium text-gray-500 uppercase">
            Agent
          </span>
          <Chip
            active={!filters.agent_id}
            onClick={() => onChange({ ...filters, agent_id: null })}
          >
            All
          </Chip>
          {agents.map((a) => (
            <Chip
              key={a.agent_id}
              active={filters.agent_id === a.agent_id}
              onClick={() => onChange({ ...filters, agent_id: a.agent_id })}
            >
              {a.name}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}
